import React, { useState } from 'react';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

interface Props {
  onEdit: () => void;
  onDelete: () => void;
  /**
   * Called when the user reaches the confirm step. Returns a sentence to show
   * first, or null for the plain two-step confirm.
   *
   * A callback rather than a value because working it out means replaying the
   * whole transaction history, and almost every row is unremarkable: paying
   * that cost per rendered row, on every render, to answer a question nobody
   * asked would be wasteful.
   */
  deleteWarning?: () => string | null;
}

// "⋯" menu with Edit and a two-step Delete confirm. Self-contained open/confirm
// state; an invisible full-screen backdrop closes it on outside click.
//
// THE TRIGGER IS ALWAYS VISIBLE, on every device and at every width.
//
// It used to be revealed on hover, which hid it completely on phones. Gating the
// reveal on `@media (hover: none)` did not fix that: some Android devices (a
// Samsung handset among them) report hover capability anyway, so the rule never
// matched and the control stayed invisible on exactly the hardware it was added
// for. Hover capability is not reliably reportable, so nothing here depends on
// it any more.
//
// Calm rather than hidden is the compromise: the icon rests at --color-sub so it
// does not compete with the figures in a dense row, and brightens to the normal
// text colour on hover and while the menu is open. A control nobody can find is
// worse than a slightly busier table.
export function RowActions({ onEdit, onDelete, deleteWarning }: Props) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const close = () => { setOpen(false); setConfirming(false); setWarning(null); };

  return (
    <div className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Actions"
        aria-label="Transaction actions"
        aria-haspopup="menu"
        aria-expanded={open}
        // 44px is the minimum comfortable tap target. Applied by WIDTH rather
        // than by hover capability, because width is something a browser always
        // reports correctly. Desktop rows keep their compact size.
        className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors max-md:min-w-[44px] max-md:min-h-[44px]"
        style={{ color: open ? 'rgb(var(--sl-100))' : 'var(--color-sub)' }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'rgb(var(--sl-100))')}
        onMouseLeave={(e) => (e.currentTarget.style.color = open ? 'rgb(var(--sl-100))' : 'var(--color-sub)')}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} />
          <div
            className="absolute right-0 top-full mt-1 z-50 rounded-lg overflow-hidden shadow-xl"
            style={{
              minWidth: warning ? 232 : 130,
              background: 'rgb(var(--sl-950))',
              border: '0.5px solid rgb(var(--overlay) / 0.12)',
            }}
          >
            <button
              type="button"
              onClick={() => { close(); onEdit(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors text-left"
              style={{ color: 'rgb(var(--sl-100))' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgb(var(--overlay) / 0.06)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            {warning && (
              <p
                className="px-3 pt-2.5 pb-1.5 text-[11px] leading-snug"
                style={{ color: 'var(--color-negative)' }}
              >
                {warning}
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                if (confirming) { onDelete(); close(); }
                else {
                  setConfirming(true);
                  setWarning(deleteWarning ? deleteWarning() : null);
                }
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors text-left"
              style={{ color: 'var(--color-negative)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(196,97,94,0.10)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {confirming ? (warning ? 'Delete anyway?' : 'Confirm delete') : 'Delete'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
